/* eslint-disable react/forbid-component-props, sonarjs/cognitive-complexity, complexity */

import classNames from 'classnames';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Element, scroller } from 'react-scroll';
import {
  SCROLL_TO_MAP_LOCATION,
  CHOOSE_YOUR_OPTIONS_COMPONENT,
  REVIEWS_COMPONENT,
  AMENITIE_COMPONENT,
} from '../../../config/constants/scroll';
import {
  DEALS_TO_CHOOSE,
  HIGHLIGHTS,
  LOCATION,
  PRODUCT_FEATURES,
  PRODUCT_SPECIFICATIONS,
  SHARE,
  CLAIM,
  GIFT_IT,
} from '../../../config/text/text';
import { isLightPage } from '../../../helpers/browser';
import { isCreditecDeal } from '../../../helpers/creditec';
import {
  getIsLocalDeal,
  getIsPaypalFeatured,
  usePins,
  showPostage,
  isClosedDeal,
  isBookingCalendarOnRedemptionDeal,
} from '../../../helpers/deals';
import { getScreenBreakpoint } from '../../../helpers/screen';
import {
  getScrollElementDealButtonType,
  mainDealLayoutType,
} from '../../../helpers/scroll';
import ThemeContext from '../../../providers/ThemeProvider';
import PayPalMessaging from '../../PayPalMessaging';
import ExpanderRelative from '../../_generic/expander/ExpanderRelative';
import WowMap from '../../_generic/wow-map/WowMap';
import BreadCrumb from '../../breadcrumb/BreadCrumb';
import BasketSocialCues from '../../checkout/BasketSocialCues';
import ClearpayMessaging from '../../clearpay/ClearpayMessaging';
import KlarnaMessaging from '../../klarna/KlarnaMessaging';
import CheckInTime from '../../travel/CheckInTime';
import LocationPin from '../../travel/LocationPin';
import MainDealAvailability from '../MainDealAvailability';
import MainDealFinePrint from '../MainDealFinePrint';
import MainDealFullDetails from '../MainDealFullDetails';
import MainDealMetaBlock from '../MainDealMetaBlock';
import MainDealRedeem from '../MainDealRedeem';
import MainDealReviewOverview from '../MainDealReviewOverview';
import MainDealReviews from '../MainDealReviews';
import MainDealShare from '../MainDealShare';
import Amenities from '../amenities/Amenities';
import AmenitiesDetails from '../amenities/AmenitiesDetails';
import GiftBoxCarousel from '../giftBoxCarousel/GiftBoxCarousel';
import InvasiveHealth from '../ihealth/InvasiveHealth';
import MainDealList from '../mainDealList';
import SectionTitle from '../shared-components/SectionTitle';
import VatText from '../shared-components/VatText';
import ChooseYourOptionsMultiSelect from '../shared-components/buy-buttons/DealChooseYourOptionsMulti';
import CompanyLogos from '../shared-components/logos/CompanyLogos';

const getButtonText = (isClaim, isExperienceBox) => {
  if (isClaim) return CLAIM;
  if (isExperienceBox) return GIFT_IT;

  return null;
};

const About = ({
  amenitiesCollapsed = true,
  deal,
  details,
  dynamicPricingReady = false,
  finePrintCollapsed,
  getButtons,
  rowSelection,
  columnSelection,
  getGiftingButton,
  headline,
  isClaim = false,
  isClearpayDeal = false,
  isExperienceBox = false,
  isGoogleHotel = false,
  isKlarnaDeal = false,
  isKlarnaPayInThree = false,
  isMobile,
  isPayPalDeal = false,
  pageDetails,
  reviewsCollapsed,
  selectedOptions,
  setAmenitiesCollapsed = () => {},
  setFinePrintCollapsed,
  setReviewsCollapsed,
  setSelectedOptions,
  setShowMultiError,
  setShowProductInfo,
  position,
  showMultiError,
  title,
  isProductPage = false,
  toReviews = () => false,
}) => {
  const router = useRouter();
  const ssr = useSelector((state) => state.deals.ssr);
  const isVipUser = useSelector((state) => state.user.userprofile?.vipUser);
  const hideDesktop = 'hide-desktop';
  const pins = usePins(deal);
  const isLocal = getIsLocalDeal(deal);
  const isPaypalFeatured = getIsPaypalFeatured(deal);
  const hasPostage = showPostage(deal, false, true, false);
  const isClosed = isClosedDeal(deal, router.query, router.pathname);
  const isEvergreen = router.query.slug.includes('e');
  const theme = useContext(ThemeContext);
  const isBookingCalendarOnRedemption = isBookingCalendarOnRedemptionDeal(deal);
  const isMarketingCreditDeal = isVipUser && deal.display.vipDiscountAllowed;
  const buttonText = getButtonText(isClaim, isExperienceBox);

  const toAmenities = () => {
    const breakpoint = getScreenBreakpoint();

    scroller.scrollTo(AMENITIE_COMPONENT, {
      delay: 100,
      duration: 1_000,
      offset: breakpoint === 'sm' ? -300 : 0,
      smooth: true,
    });
    setAmenitiesCollapsed(false);
  };

  return (
    <>
      <div className="deal-main-deal__about">
        {!isProductPage && (
          <h1
            className={classNames('deal-main-deal__title', {
              'deal-main-deal__title--no-margin': hasPostage,
              'hide-desktop': !pageDetails.isTravel,
            })}
          >
            {headline}
          </h1>
        )}
        {pageDetails.isTravel && deal?.travelHotelResortName && (
          <h2 className="deal-main-deal__resort_hotel_title">
            {deal.travelHotelResortName}
          </h2>
        )}
        <MainDealMetaBlock
          columnSelection={columnSelection}
          deal={deal}
          getGiftingButton={getGiftingButton}
          hasPostage={hasPostage}
          isClaim={isClaim}
          isClosed={isClosed}
          isMobile={isMobile}
          isProductPage={isProductPage}
          pageDetails={pageDetails}
          position={position}
          reviewsCollapsed={reviewsCollapsed}
          rowSelection={rowSelection}
          setReviewsCollapsed={setReviewsCollapsed}
          splatVisible={!ssr}
        />
        {!isClosed &&
          !isProductPage &&
          getButtons(
            'mobile',
            pageDetails,
            getScrollElementDealButtonType(deal, mainDealLayoutType.about),
          )}
        {!isClosed &&
          isProductPage &&
          isMobile &&
          getButtons(
            'mobile',
            pageDetails,
            getScrollElementDealButtonType(deal, mainDealLayoutType.about),
          )}
        <Amenities
          amenities={deal?.amenitiesList}
          isTravel={pageDetails.isTravel}
          toAmenities={toAmenities}
        />
        <div>
          {!pageDetails.isTravel && isMobile && (
            <MainDealReviewOverview
              deal={deal}
              isTravel={pageDetails.isTravel}
              toReviews={toReviews}
            />
          )}
        </div>
        {!isCreditecDeal(deal) && (
          <>
            <div className="deal-main-deal__onsite-messaging">
              <KlarnaMessaging
                deal={deal}
                isKlarnaDeal={isKlarnaDeal}
                isKlarnaPayInThree={isKlarnaPayInThree}
                isMarketingCreditDeal={isMarketingCreditDeal}
              />
            </div>
            <div className="deal-main-deal__onsite-messaging">
              <ClearpayMessaging
                deal={deal}
                isClearpayDeal={isClearpayDeal}
                isMarketingCreditDeal={isMarketingCreditDeal}
              />
            </div>
            <div className="deal-main-deal__onsite-messaging">
              {!isClaim && (
                <PayPalMessaging
                  deal={deal}
                  isFeatured={isPaypalFeatured}
                  isFeaturedSlot={false}
                  isMarketingCreditDeal={isMarketingCreditDeal}
                  isPayPalDeal={isPayPalDeal}
                />
              )}
            </div>
          </>
        )}
        <div className="deal-main-deal__info-section">
          <p
            className={classNames(
              'deal-main-deal__subtitle',
              pageDetails.isTravel ? true : hideDesktop,
              {
                visible: !ssr,
              },
            )}
          >
            {title}
          </p>
          <div
            className={classNames({
              'hide-desktop': pageDetails.isTravel,
            })}
          >
            <Element name={CHOOSE_YOUR_OPTIONS_COMPONENT} />
            <ChooseYourOptionsMultiSelect
              className="cyo-main-deal"
              deal={deal}
              dynamicPricingReady={dynamicPricingReady}
              isClaim={isClaim}
              isExperienceBox={isExperienceBox}
              isProductPage={isProductPage}
              isSoldOut={isClosed}
              onChangeValues={(options) => setSelectedOptions(options)}
              onSetShowMultiError={(show) => setShowMultiError(show)}
              selectedOptions={selectedOptions}
              showError={showMultiError}
              text={buttonText}
            />
          </div>
          {pageDetails.isTravel && (
            <LocationPin
              deal={deal}
              scrollTarget={SCROLL_TO_MAP_LOCATION}
              theme={theme}
            />
          )}
          <CheckInTime
            deal={deal}
            isTravel={pageDetails.isTravel}
            theme={theme}
          />
          <MainDealList
            className="highlights"
            list={deal.highlights}
            title={isLocal ? HIGHLIGHTS : PRODUCT_FEATURES}
          />
          {isMobile && (
            <div className="deal-main-deal__social-cues">
              {!isCreditecDeal(deal) && <BasketSocialCues />}
            </div>
          )}
          <MainDealList
            className="specifications"
            list={deal.specificationList}
            title={PRODUCT_SPECIFICATIONS}
          />
          <InvasiveHealth deal={deal} mobile />
          <ExpanderRelative
            marginBottom={MainDealFullDetails.MARGIN_BOTTOM}
            maxHeight={MainDealFullDetails.MAX_HEIGHT}
          >
            <MainDealFullDetails
              contentVisible={!ssr}
              deal={deal}
              isGoogleHotel={isGoogleHotel}
            />
          </ExpanderRelative>
          {pins && pins.length > 0 && (
            <>
              <Element name={SCROLL_TO_MAP_LOCATION} />
              <SectionTitle title={LOCATION} />
              {!isLightPage() && (
                <WowMap
                  maxZoom={15}
                  pins={pins}
                  zoom={deal?.mapZoomDefaultSize}
                />
              )}
            </>
          )}
        </div>
        {!isLightPage() &&
          isExperienceBox &&
          deal?.display?.guidePageLocalPath && (
            <div className="deal-main-deal__experiences-section">
              <SectionTitle title={DEALS_TO_CHOOSE} />
              <GiftBoxCarousel
                guidePageSlug={deal?.display?.guidePageLocalPath}
                isTravel={pageDetails.isTravel}
              />
            </div>
          )}
        {isMobile && (
          <div className="deal-main-deal__share-section">
            <SectionTitle title={SHARE} />
            <MainDealShare deal={deal} />
            <CompanyLogos deal={deal} />
          </div>
        )}
        <div className="deal-main-deal__admin-section">
          {!isExperienceBox && !isCreditecDeal(deal) && (
            <MainDealRedeem deal={deal} details={details} />
          )}
          <Element name={AMENITIE_COMPONENT} />
          <AmenitiesDetails
            amenities={deal?.amenitiesList}
            collapsed={amenitiesCollapsed}
            isTravel={pageDetails.isTravel}
            onToggle={() => setAmenitiesCollapsed((state) => !state)}
          />
          <>
            <Element name={REVIEWS_COMPONENT} />
            <MainDealReviews
              collapsed={reviewsCollapsed}
              deal={deal}
              isTravel={pageDetails.isTravel}
              onToggle={() => setReviewsCollapsed((state) => !state)}
              pagination
            />
          </>
          {isBookingCalendarOnRedemption && (
            <MainDealAvailability deal={deal} isTravel={pageDetails.isTravel} />
          )}
          <MainDealFinePrint
            collapsed={finePrintCollapsed}
            deal={deal}
            onToggle={() => setFinePrintCollapsed((state) => !state)}
          />
        </div>
        <div className="deal-main-deal__vat-text--mobile">
          <VatText deal={deal} setShowProductInfo={setShowProductInfo} />
        </div>
        {isEvergreen && (
          <div className="deal-main-deal__sku--mobile">SKU:&nbsp;{deal.id}</div>
        )}
        {isMobile && (
          <BreadCrumb className="deal-main-deal__breadcrumbs" deal={deal} />
        )}
      </div>
      <style jsx>{`
        .deal-main-deal__about {
          position: relative;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          padding: 0 16px 8px 16px;
          width: 100%;
        }

        .deal-main-deal__sku--mobile {
          display: none;
          margin: 0 0 15px;
          font-size: 12px;
        }

        .deal-main-deal__share-section,
        .deal-main-deal__experiences-section {
          margin-bottom: 20px;
        }

        :global(.deal-main-deal__about .share) {
          margin-bottom: 20px;
        }

        :global(.main-deal-travel .deal-main-deal__about) {
          padding-top: 8px;
        }

        .deal-main-deal__about h1 {
          font-size: 18px;
        }

        .deal-main-deal__title--no-margin {
          margin-bottom: 0;
        }

        .deal-main-deal__subtitle {
          visibility: hidden;
        }

        .deal-main-deal__subtitle.visible {
          visibility: visible;
        }

        :global(.main-deal-travel h2.deal-main-deal__resort_hotel_title) {
          width: calc(100% - 85px);
          font-style: italic;
          font-size: 16px;
        }

        @media (min-width: ${theme.breakpoints.mdUp}) {
          .deal-main-deal__about h1 {
            font-size: 22px;
          }
          :global(.main-deal-travel h2.deal-main-deal__resort_hotel_title) {
            font-size: 20px;
          }
        }

        :global(.main-deal-travel p.deal-main-deal__subtitle) {
          font-size: 17px;
        }

        @media (max-width: ${theme.breakpoints.mdDown}) {
          .deal-main-deal__about {
            width: 100%;
            min-height: 105px;
            padding: 8px 16px;
          }
          :global(.deal-main-deal__about .share) {
            margin-bottom: 20px;
          }

          .deal-main-deal__sku--mobile {
            display: block;
          }

          .deal-main-deal__about h1 {
            width: calc(100% - 85px);
          }
          /* Move all elements to the end... */
          :global(.deal-main-deal__about > :nth-child(n)) {
            order: 3;
          }
          /* ...expect first 2/4 which are at the begining... */
          :global(.deal-main-deal__about
              > :nth-child(-n + ${isPaypalFeatured ? 2 : 5})) {
            order: 1;
          }
          /* ...the paypal module is btween the two groups */
          :global(.deal-main-deal__about > .paypal) {
            order: 2;
          }

          :global(.deal-main-deal__breadcrumbs.breadcrumb-container) {
            margin-top: 16px;
            order: 5;
          }

          @media (max-width: ${theme.breakpoints.smDown}) {
            :global(.deal-main-deal__about .maindeal-combined-button) {
              order: 4;
              margin-left: -16px;
              width: calc(100% + 32px);
            }
          }
        }

        @media (min-width: ${theme.breakpoints.xlUp}) {
          .deal-main-deal__vat-text--mobile {
            display: none;
          }
        }
        .deal-main-deal__onsite-messaging {
          padding: 5px 0px;
        }
      `}</style>
    </>
  );
};

About.propTypes = {
  amenitiesCollapsed: PropTypes.bool,
  columnSelection: PropTypes.number,
  deal: PropTypes.object.isRequired,
  details: PropTypes.object,
  dynamicPricingReady: PropTypes.bool,
  finePrintCollapsed: PropTypes.bool.isRequired,
  getButtons: PropTypes.func.isRequired,
  getGiftingButton: PropTypes.func.isRequired,
  headline: PropTypes.string.isRequired,
  isClaim: PropTypes.bool,
  isClearpayDeal: PropTypes.bool,
  isExperienceBox: PropTypes.bool,
  isGoogleHotel: PropTypes.bool,
  isKlarnaDeal: PropTypes.bool,
  isKlarnaPayInThree: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPayPalDeal: PropTypes.bool,
  isProductPage: PropTypes.bool,
  pageDetails: PropTypes.object.isRequired,
  position: PropTypes.any,
  reviewsCollapsed: PropTypes.bool.isRequired,
  rowSelection: PropTypes.number,
  selectedOptions: PropTypes.array.isRequired,
  setAmenitiesCollapsed: PropTypes.func,
  setFinePrintCollapsed: PropTypes.func.isRequired,
  setReviewsCollapsed: PropTypes.func.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  setShowMultiError: PropTypes.func.isRequired,
  setShowProductInfo: PropTypes.func,
  showMultiError: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  toReviews: PropTypes.func,
};

export default About;
